<template>
  <div
    :class="{
      funnel: !invitationsResultsLoading && !contentsLoading && (!isNewUser || isMasterView),
      'funnel-new-user':
        !invitationsResultsLoading && !contentsLoading && isNewUser && !isMasterView,
    }"
  >
    <v-skeleton-loader
      v-if="invitationsResultsLoading || contentsLoading"
      :style="{ width: $vuetify.display.mdAndUp ? '97%' : '100%' }"
      class="mx-auto"
      type="article, actions"
    />
    <v-card
      v-else
      :class="{ 'card-new-user': isNewUser && !isMasterView }"
      class="pa-3 pb-0 card"
    >
      <v-card-title
        id="contact-acquisition-title"
        class="mb-2 pa-0 title d-flex justify-space-between"
        >{{ $t('dashboard.contact-acquisition.title') }}
        <span
          v-if="showViewDetailsLink"
          class="mt-2 mr-2 details-label"
          @click="emit('open-details', invitationsPerLandingpage)"
        >
          {{ $t('dashboard.details.icon-label') }}
        </span>
      </v-card-title>

      <BlurredBackdropUpselling v-if="needsUpgrade" />
      <template v-else>
        <v-card-text
          v-if="isNewUser && !isMasterView"
          class="pa-0"
        >
          <div class="d-flex justify-space-between gap-1 mb-4">
            <Permission permission="lead:invite-bulk">
              <Feature :feature-slug="featureNames.ACQUISITION_BOOSTER">
                <div class="d-flex flex-column align-start">
                  <span class="label mb-3">
                    {{ $t('dashboard.contact-acquisition.new-user-labels.0') }}
                  </span>
                  <v-btn
                    v-if="!isMasterView"
                    :to="{
                      name: 'leads-view',
                      params: { view: 'list' },
                      query: { showBooster: 'true' },
                    }"
                    @click="$tracking.event('Dashboard', 'Clicked', 'Leads')"
                    color="white"
                    class="mt-auto"
                  >
                    <span
                      class="button text-primary"
                      v-if="$vuetify.display.smAndDown"
                    >
                      {{ $t('buttons.dashboard.contact-acquisition.new-user-labels-mobile.0') }}
                    </span>
                    <span
                      class="button text-primary"
                      v-else
                    >
                      {{ $t('buttons.dashboard.contact-acquisition.new-user-labels.0') }}
                    </span>
                  </v-btn>
                </div>
              </Feature>
            </Permission>
            <Permission permission="ai:read">
              <Feature :feature-slug="featureNames.CONTENT_CREATOR">
                <div class="d-flex flex-column align-start ml-md-2">
                  <span class="label mb-3">
                    {{ $t('dashboard.contact-acquisition.new-user-labels.1') }}
                  </span>
                  <v-btn
                    v-if="!isMasterView"
                    @click="createAndPost()"
                    color="white"
                    class="mt-auto"
                  >
                    <span
                      class="button text-primary"
                      v-if="$vuetify.display.smAndDown"
                    >
                      {{ $t('buttons.dashboard.contact-acquisition.new-user-labels-mobile.1') }}
                    </span>
                    <span
                      class="button text-primary"
                      v-else
                    >
                      {{ $t('buttons.dashboard.contact-acquisition.new-user-labels.1') }}
                    </span>
                  </v-btn>
                </div>
              </Feature>
            </Permission>
          </div>
        </v-card-text>
        <v-card-text
          v-else
          class="pa-0 mb-1"
        >
          <div class="d-flex justify-space-between gap-1">
            <Permission permission="lead:invite-bulk">
              <Feature :feature-slug="featureNames.ACQUISITION_BOOSTER">
                <div style="display: flex; flex-direction: column">
                  <span class="label mb-1">{{
                    $t('dashboard.contact-acquisition.existing-user-labels.0')
                  }}</span>
                  <div class="d-flex my-md-3 values-wrapper">
                    <span class="d-flex align-center">
                      <v-avatar
                        color="rgba(255, 255, 255, 0.2)"
                        size="36"
                      >
                        <v-icon
                          icon="mdi-account-plus"
                          color="white"
                        />
                      </v-avatar>
                    </span>
                    <div class="d-flex flex-column ml-md-2 ml-1">
                      <span class="label">{{
                        $t('dashboard.contact-acquisition.existing-user-labels.1')
                      }}</span>
                      <span class="grid">
                        <span class="number">{{
                          totalInvitationsCount?.toLocaleString() || 0
                        }}</span>
                      </span>
                    </div>
                  </div>
                  <v-btn
                    v-if="!isMasterView"
                    :to="{
                      name: 'leads-view',
                      params: { view: 'list' },
                      query: { showBooster: 'true' },
                    }"
                    @click="$tracking.event('Dashboard', 'Clicked', 'Leads')"
                    class="button-invite-more mt-3 mt-md-0"
                    color="white"
                  >
                    <span class="button text-primary">
                      {{ $t('buttons.dashboard.contact-acquisition.existing-user-labels.0') }}
                    </span>
                  </v-btn>
                </div>
              </Feature>
            </Permission>
            <Permission permission="ai:read">
              <Feature :feature-slug="featureNames.CONTENT_CREATOR">
                <div style="display: flex; flex-direction: column">
                  <span class="label mb-1"
                    >{{ $t('dashboard.contact-acquisition.existing-user-labels.2') }}
                  </span>
                  <div class="d-flex my-md-3 values-wrapper">
                    <span class="d-flex align-center">
                      <v-avatar
                        color="rgba(255, 255, 255, 0.2)"
                        size="36"
                      >
                        <v-icon
                          icon="mdi-brain"
                          color="white"
                        />
                      </v-avatar>
                    </span>
                    <div class="d-flex flex-column ml-md-2 ml-1">
                      <div class="label">
                        {{ $t('dashboard.contact-acquisition.existing-user-labels.3') }}
                      </div>
                      <span class="grid">
                        <span class="number"
                          >{{ savedContentsTotal?.toLocaleString() || 0 }} /
                          {{ savedContentsNumberOfPosts?.toLocaleString() || 0 }}</span
                        >
                      </span>
                    </div>
                  </div>
                  <v-btn
                    v-if="!isMasterView"
                    @click="doMore()"
                    class="button-do-more mt-3 mt-md-0"
                    color="white"
                  >
                    <span class="button text-primary">{{
                      $t('buttons.dashboard.contact-acquisition.existing-user-labels.1')
                    }}</span>
                  </v-btn>
                </div>
              </Feature>
            </Permission>
          </div>
          <v-divider class="my-3" />
          <div class="save-time-money mb-4">
            <span class="label mr-1 mr-md-4">{{
              $t('dashboard.contact-acquisition.existing-user-labels.4')
            }}</span>
            <span class="save-tme-money-numbers">
              <v-icon
                class="mr-1"
                color="white"
              >
                mdi-clock-time-four-outline
              </v-icon>
              <span class="number save-tme-money-number">{{
                formatMinutesToHours(timeSaved)
              }}</span>
              <span class="px-1 px-md-3 vertical-divider">|</span>
              <v-icon
                class="mr-1"
                color="white"
              >
                mdi-cash-multiple
              </v-icon>
              <span class="number save-tme-money-number">{{ formatPrice(moneySaved) }}</span>
            </span>
          </div>
        </v-card-text>
      </template>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable'
import { computed, getCurrentInstance, inject, ref, watch, watchEffect } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import Permission from '@/components/Permission'
import { useBranding } from '@/composables/useBranding'
import featureNames from '@/lib/featureNames'
import formatMinutesToHours from '@/lib/time'
import currencyMixin from '@/mixins/currency'
import featureMixin from '@/mixins/feature'
import INVITATION_STATISTICS_FOR_COMPANY from '@/modules/acquisitionBooster/InvitationStatisticsForCompany.gql'
import GET_SAVED_CONTENTS from '@/modules/contentCreator/ContentCreatorDasboard/getSavedContents.gql'
import { Feature } from '@/plugins/feature-config'

import {
  DashboardContext,
  dashboardContextDefault,
  DashboardContextSymbol,
} from './dashboardContext'

const router = useRouter()
const route = useRoute()
const { appContext } = getCurrentInstance()!
const { dashboardBoxesBackground } = useBranding()
const $tracking = appContext.config.globalProperties.$tracking
const $features = appContext.config.globalProperties.$features
const instance = getCurrentInstance()
const dashboardContext = inject<DashboardContext>(DashboardContextSymbol, dashboardContextDefault)
const formatPrice = currencyMixin.methods.formatPrice.bind(instance.proxy)
const getFeature = featureMixin.methods.getFeature.bind(instance.proxy)

defineProps({
  isNewUser: Boolean,
  isLightUser: Boolean,
  showViewDetailsLink: Boolean,
})

const MINUTES_PER_POST = 45
const MONEY_PER_MINUTE = 2.5
const MINUTES_PER_MAIL = 25

const cardColors = computed(() => {
  return { ...dashboardBoxesBackground.value['box1'] }
})

const factors = computed(() => {
  const dashboardConfig = $features?.feature(featureNames.CONTENT_CREATOR)?.config?.dashboard
  return {
    minutesPerPost: dashboardConfig?.minutesPerPost || MINUTES_PER_POST,
    moneyPerMinute: dashboardConfig?.moneyPerMinute || MONEY_PER_MINUTE,
  }
})

const isMasterView = computed(() => route.path.startsWith('/master'))
const routeParamId = computed(() => route?.params?.id || null)

const savedContentVariables = computed(() => ({
  input: {
    perPage: 1,
    page: 1,
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value,
    ...dashboardContext.formattedDateFilters.value,
  },
}))

const {
  result: contents,
  loading: contentsLoading,
  refetch: refetchContents,
} = useQuery(GET_SAVED_CONTENTS, savedContentVariables)

const savedContentsTotal = computed(() => contents.value?.savedContents.total)
const savedContentsNumberOfPosts = computed(() => contents.value?.savedContents.numberOfPosts)

const savedContentsTimeSaved = computed(() =>
  contents.value?.savedContents.total
    ? contents.value?.savedContents.total * factors.value.minutesPerPost
    : null
)

const savedContentsMoneySaved = computed(() =>
  contents.value?.savedContents.total
    ? contents.value?.savedContents.total *
      factors.value.minutesPerPost *
      factors.value.moneyPerMinute
    : null
)

const variables = computed(() => ({
  input: {
    isMasterView: isMasterView.value,
    childCompanyId: routeParamId.value ?? null,
    ...dashboardContext.formattedDateFilters.value,
  },
}))
const {
  result: invitationsResults,
  loading: invitationsResultsLoading,
  refetch: refetchInvitations,
} = useQuery(INVITATION_STATISTICS_FOR_COMPANY, variables)

const invitationsPerLandingpage = computed(() =>
  invitationsResults.value?.invitationStatisticsForCompany?.invitationsPerLandingpage?.map(
    (invitation) => {
      return {
        label: invitation.landingpageName,
        value: invitation.total,
      }
    }
  )
)

const totalInvitationsCount = computed(
  () => invitationsResults.value?.invitationStatisticsForCompany?.total
)
const invitationFactors = computed(() => {
  const config = $features?.feature(featureNames.ACQUISITION_BOOSTER)?.config?.dashboard
  const { minutesPerMail = MINUTES_PER_MAIL, moneyPerMinute = MONEY_PER_MINUTE } = config || {}
  return {
    minutesPerMail,
    moneyPerMinute,
  }
})

const invitationsTimeSaved = computed(
  () =>
    totalInvitationsCount.value &&
    totalInvitationsCount.value * invitationFactors.value.minutesPerMail
)

const invitationsMoneySaved = computed(
  () =>
    totalInvitationsCount.value &&
    totalInvitationsCount.value *
      invitationFactors.value.minutesPerMail *
      invitationFactors.value.moneyPerMinute
)

const timeSaved = computed(
  () => (savedContentsTimeSaved.value || 0) + (invitationsTimeSaved.value || 0)
)
const moneySaved = computed(
  () => (savedContentsMoneySaved.value || 0) + (invitationsMoneySaved.value || 0)
)

const emit = defineEmits(['update-stats', 'open-details'])

watchEffect(() => {
  if (timeSaved.value && moneySaved.value) {
    emit('update-stats', { timeSaved: timeSaved.value, moneySaved: moneySaved.value })
  }
})

const aquisitionBoosterFeature = computed(() => getFeature(featureNames.ACQUISITION_BOOSTER))
const contentCreatorFeature = computed(() => getFeature(featureNames.CONTENT_CREATOR))

const needsUpgrade = ref(
  !aquisitionBoosterFeature.value.isActive && !contentCreatorFeature.value.isActive
)
const doMore = async () => {
  $tracking.event('Dashboard', 'Clicked', 'Content Creator')
  router.push({ name: 'content-creator' })
}

const createAndPost = async () => {
  $tracking.event('Dashboard', 'Clicked', 'Content Creator')
  router.push({ name: 'content-creator' })
}

watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId !== oldId) {
      await refetchContents({
        input: {
          perPage: 1,
          page: 1,
          isMasterView: isMasterView.value,
          childCompanyId: newId,
        },
      })
      await refetchInvitations({
        input: { isMasterView: isMasterView.value, childCompanyId: newId },
      })
    }
  }
)
</script>

<style scoped>
.details-label {
  cursor: pointer;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  text-decoration: underline;
}
.progress-bar {
  margin: auto;
}
.product-fruits-icon {
  position: absolute;
  right: 10px;
  top: 10px;
}
:deep(.blur-layer) {
  background: transparent;
}

.funnel::after {
  content: '';
  position: absolute;
  top: 99%;
  left: 15px;
  right: 15px;
  border-top: solid 20px v-bind('cardColors.border');
  border-left: solid 50px transparent;
  border-right: solid 50px transparent;
}
.card {
  width: 97%;
  margin: 0 auto 0 auto;
  background: v-bind('cardColors.base');
}

.funnel-new-user::after {
  content: '';
  position: absolute;
  top: 99%;
  left: 30px;
  right: 30px;
  border-top: solid 20px v-bind('cardColors.border');
  border-left: solid 50px transparent;
  border-right: solid 50px transparent;
}

.card-new-user {
  width: 92%;
  background: v-bind('cardColors.base');
}

@media (max-width: 960px) {
  .values-wrapper {
    margin-top: auto;
  }
  .v-card {
    margin: 0;
    width: 100%;
  }
  .save-tme-money-number {
    min-width: max-content;
  }
}
@media (max-width: 400px) {
  .button {
    font-size: 11px;
  }
}

@media (max-width: 600px) {
  :deep(.centered-alert) {
    width: 100% !important;
  }
}

@media (max-width: 350px) {
  .details-label {
    font-size: 10px;
  }
}
</style>
